import { useEffect, useRef, useState } from 'react';
import { I18nProvider, useI18n } from '@react/i18n';
import { BasketSummary, BasketSummaryItem } from '@components/checkout/types';

/**
 * Boční souhrn objednávky na kroku 3 — React náhrada BasketControl::renderSide
 * (side.latte; používal se jen na této stránce).
 *
 * Reaguje živě na VAT stav formuláře: CheckoutForm po každé VIES validaci /
 * ruční volbě plátce dispatchne `checkout:vat-changed` s čerstvým souhrnem
 * ze serveru (BasketSummaryFactory) a tady se přepne zvýraznění s/bez DPH.
 * Ceny přicházejí formátované server-side (stejný |currency filtr jako Latte).
 */

interface Props {
    translationsUrl: string | null;
    initialSummary: BasketSummary;
}

export default function BasketSummarySide(props: Props) {
    return (
        <I18nProvider url={props.translationsUrl}>
            <SummaryInner initialSummary={props.initialSummary} />
        </I18nProvider>
    );
}

function SummaryInner({ initialSummary }: { initialSummary: BasketSummary }) {
    const t = useI18n();
    const [summary, setSummary] = useState<BasketSummary>(initialSummary);
    const recapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<BasketSummary>).detail;
            if (detail && Array.isArray(detail.items)) {
                setSummary(detail);
            }
        };
        window.addEventListener('checkout:vat-changed', handler);

        return () => window.removeEventListener('checkout:vat-changed', handler);
    }, []);

    useEffect(() => {
        const recap = recapRef.current;
        const footer = document.querySelector<HTMLElement>('.s_footer');
        if (!recap || !footer) {
            return;
        }

        let start = recap.getBoundingClientRect().top + window.scrollY - 20;
        let frame = 0;

        const update = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const footerTop = footer.getBoundingClientRect().top + window.scrollY;
                const end = footerTop - recap.offsetHeight - 120;
                const fixed = window.innerWidth > 767 && window.scrollY > start && window.scrollY < end;
                recap.classList.toggle('fixed', fixed);
            });
        };

        const resize = () => {
            recap.classList.remove('fixed');
            start = recap.getBoundingClientRect().top + window.scrollY - 20;
            update();
        };

        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', resize);
        update();

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', resize);
            recap.classList.remove('fixed');
        };
    }, []);

    const canDph = summary.highlightWithoutTax ?? summary.reverseCharge;

    return (
        <div className="basket__s3-cover__right">
            <div>
                <div ref={recapRef} id="recap-orders-div" className={`recap-orders${summary.loggedIn ? ' recap-orders--logged' : ''}`}>
                    <p className="recap-orders__title">{t('Objednáváte')}</p>

                    {summary.items.map((item, index) => (
                        <SummaryItem key={index} item={item} canDph={canDph} />
                    ))}

                    <div className="recap-orders__item recap-orders__item--sum clearfix">
                        <span className="recap-orders__item__img">
                            <strong>{t('Celkem')}</strong>
                        </span>
                        <span className="recap-orders__item__content">
                            <span className={canDph ? 'recap-orders__item__price' : 'recap-orders__item__title'}>
                                {summary.totalWithTax} {t('s DPH')}
                            </span>
                            <span className={!canDph ? 'recap-orders__item__price' : 'recap-orders__item__title'}>
                                {summary.totalWithoutTax} {t('bez DPH')}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Ceny s/bez DPH — zvýrazněná je ta, která bude na objednávce (dle reverse-charge). */
function Prices({ item, canDph }: { item: BasketSummaryItem; canDph: boolean }) {
    const t = useI18n();

    if (item.free) {
        return <span className="recap-orders__item__price">{firstUpper(t('zdarma'))}</span>;
    }

    return canDph ? (
        <>
            <span className="out_of_stock">{item.priceWithTax} {t('s DPH')}</span>
            <span className="recap-orders__item__price">{item.priceWithoutTax} {t('bez DPH')}</span>
        </>
    ) : (
        <>
            <span className="recap-orders__item__price">{item.priceWithTax} {t('s DPH')}</span>
            <span className="out_of_stock">{item.priceWithoutTax} {t('bez DPH')}</span>
        </>
    );
}

function SummaryItem({ item, canDph }: { item: BasketSummaryItem; canDph: boolean }) {
    const t = useI18n();

    if (item.kind === 'product' || item.kind === 'gift' || item.kind === 'giftChild') {
        const content = (
            <>
                <span className="recap-orders__item__img">
                    {item.imageUrl && <img src={item.imageUrl} alt={item.name} />}
                </span>
                <span className="recap-orders__item__content">
                    <span className="recap-orders__item__num">{`${item.amount}\u00a0${item.amountUnit}`}</span>
                    <span className="recap-orders__item__title">{item.name}</span>
                    {item.kind === 'giftChild' && (
                        item.outOfStock
                            ? <em className="out_of_stock">{item.availability}</em>
                            : <em>{t('U vás doma za')} {item.availability}</em>
                    )}
                    <Prices item={item} canDph={canDph} />
                </span>
            </>
        );

        return item.productUrl
            ? <a href={item.productUrl} className="recap-orders__item clearfix">{content}</a>
            : <div className="recap-orders__item clearfix">{content}</div>;
    }

    const iconClass = item.kind === 'delivery'
        ? `recap-orders__item__img recap-orders__item__img--transport${item.pickupIcon ? '-h' : ''}`
        : item.kind === 'payment'
            ? 'recap-orders__item__img recap-orders__item__img--pay'
            : 'recap-orders__item__img recap-orders__item__img--voucher';

    return (
        <div className="recap-orders__item recap-orders__item--no-link clearfix">
            <span className={iconClass} />
            <span className="recap-orders__item__content">
                <span className="recap-orders__item__title">{item.name}</span>
                <Prices item={item} canDph={canDph} />
            </span>
        </div>
    );
}

function firstUpper(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}
